import { useEffect, useRef, useState } from 'react';

const DURATION = 700;

/**
 * Animates a number from whatever was last shown up to `target`.
 *
 * The balance is the whole point of a redemption, so it counts up rather than
 * snapping: the customer sees the coins arrive. Returns `target` untouched on
 * the first value it is handed, on a non-numeric value, and when the reader
 * has asked for reduced motion, so callers never special-case loading.
 */
export const useCountUp = (target) => {
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);

  useEffect(() => {
    const from = fromRef.current;
    const skip =
      typeof from !== 'number' ||
      typeof target !== 'number' ||
      from === target ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (skip) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }

    let raf;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / DURATION, 1);
      // Cubic ease-out: quick off the mark, settles onto the final number.
      const eased = 1 - Math.pow(1 - progress, 3);
      setShown(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return shown;
};

export default useCountUp;
